import Konva from 'konva';
import { Layer, Stage } from 'react-konva';
import { useEventListener } from 'ahooks';
import { useHotkeys } from '@mantine/hooks';
import { useCallback, useEffect, useRef } from 'react';
import { notifyError, notifySuccess } from '@shakkerai/pro-components';
import { useViewport, useTempState, getTempState, setTempState, getViewport } from '@starflow/app/store';
import { getProjectState, getSharedData, setSharedData, setViewport } from '@starflow/app/store';
import { clearSelection, updateSelection, removeElements } from '@starflow/app/helpers/canvas';
import { translate } from '@shakkerai/util-i18n';
import { mc } from '@shakkerai/report';
import {
  copyImageToClipboard,
  insertHistoryImage,
  insertImage,
  insertLocalImage,
  insertLocalImages,
} from '@shakkerai/pro-utils';
import { duplicateElements } from '@starflow/app/helpers/element';
import { CustomCursor } from './Cursor';

const ZOOM_SCALE_BY = 1.2;
const MAX_SCALE = 2;
const MIN_SCALE = 0.02;

const dragButtons = [0, 1];

let sharedStage = null;
let lastZoomingAt = null;

const eventPreventDefault = (event) => event.preventDefault();

export const getSharedStage = () => sharedStage;

const handleGlobalWheel = (event) => {
  if (event.ctrlKey) {
    event.preventDefault();
    event.stopPropagation();
  }
};

const handleGlobalDrop = async (event) => {
  const { x, y, scale } = getViewport();

  const targetX = event.pageX / scale - x / scale;
  const targetY = event.pageY / scale - y / scale;

  try {
    if (event?.dataTransfer?.files?.length > 0) {
      insertLocalImages(Array.from(event.dataTransfer.files), {
        position: { x: targetX, y: targetY },
        from: 'canvas',
      });
      return event.preventDefault();
    }

    const image = getSharedData('dragging-element');
    setSharedData('dragging-element', null);

    if (!image) return;

    const [offsetX, offsetY] = getSharedData('dragging-offset') || [0, 0];
    setSharedData('dragging-offset', null);

    if (image.source === 'history') {
      insertHistoryImage({
        ...image,
        x: targetX - offsetX,
        y: targetY - offsetY,
      });
      return;
    }

    insertImage({
      url: image.url,
      x: targetX - offsetX,
      y: targetY - offsetY,
      width: image.width,
      height: image.height,
    });
  } catch (error) {
    console.log(error);
    notifyError(error.message);
  }
};

export const CanvasStage = (props) => {
  const stageRef = useRef(null);

  const viewport = useViewport();
  const dragging = useTempState('dragging');
  const spacePressing = useTempState('spacePressing');
  const currentCanvasTool = useTempState('currentCanvasTool');

  const canvasDragging = spacePressing || currentCanvasTool === 'move';

  const handleDragStart = useCallback((event) => {
    const draggingIcon = event.target?.getAttr('draggingIcon');

    if (draggingIcon) {
      const selection = getProjectState('selection');

      if (selection.includes(event.target.name())) {
        if (selection.length > 1) return;
      }

      const originX = event.target.x();
      const originY = event.target.y();
      const stageScale = stageRef.current.scaleX();
      const pointer = stageRef.current.getPointerPosition();

      const offsetX = (pointer.x - stageRef.current.x()) / stageScale - originX;
      const offsetY = (pointer.y - stageRef.current.y()) / stageScale - originY;

      const width = event.target.width() * event.target.scaleX();
      const height = event.target.height() * event.target.scaleY();
      const element = getProjectState('elements').find((item) => item.id === event.target.name());

      if (!element) return;

      setSharedData('dragging-element', {
        id: element.id,
        type: element.type,
        url: draggingIcon,
        innerTaskId: element.innerTaskId,
        originX,
        originY,
        offsetX,
        offsetY,
        width,
        height,
      });
    }
  }, []);

  // 同步画布移动
  const handleDragMove = useCallback(
    (event) => {
      if (canvasDragging && event.target instanceof Konva.Stage) {
        setViewport({ x: event.target.x(), y: event.target.y() });
      }
    },
    [canvasDragging],
  );

  const handleDragEnd = useCallback(() => {
    setTempState({ dragging: false, selectionRect: null });
  }, []);

  const handleClick = useCallback((event) => {
    if (event.evt && event.evt.button === 1) {
      return;
    }

    if (getSharedData('shouldPreventStageClick')) {
      setSharedData('shouldPreventStageClick', false);
      return;
    }

    const dragging = getTempState('dragging');
    const spacePressing = getTempState('spacePressing');
    const currentCanvasTool = getTempState('currentCanvasTool');
    const selectionDisabled = dragging || spacePressing || currentCanvasTool !== 'select';

    if (selectionDisabled) {
      return;
    }

    if (event.target instanceof Konva.Stage) {
      clearSelection();
    }

    let target = event.target;
    let targetElementId = null;

    const role = event.target?.getAttr('role');

    if (event.target instanceof Konva.Shape && event.target.parent instanceof Konva.Transformer) {
      //...
    } else if (role === 'element') {
      while (target.parent?.getAttr('role') === 'element') {
        target = target.parent;
      }

      targetElementId = target.name();

      const targetWrapper = stageRef.current.findOne(`#${targetElementId}-wrapper`);

      if (targetWrapper && targetWrapper.attrs.groupId) {
        targetElementId = targetWrapper.attrs.groupId;
      }

      if (event.evt.button === 0) {
        // 如果元素正在编辑中, 单击不要退出
        if (getProjectState('editingElementId')) return;
        if (event.evt.shiftKey) {
          const currentSelection = getProjectState('selection');
          const newSelecton = currentSelection.concat([targetElementId]);
          updateSelection([...new Set(newSelecton)]);
        } else {
          updateSelection([targetElementId]);
        }
      }

      if (document.activeElement) {
        document.activeElement.blur?.();
      }
    }

    if (event.evt.button === 2) {
      const selection = getProjectState('selection');

      let contextMenuTarget = 'canvas';
      let contextMenuData = null;

      if (selection.length > 1 && selection.includes(targetElementId)) {
        contextMenuTarget = 'selection';
        contextMenuData = selection;
      } else if (role === 'element') {
        contextMenuTarget = 'element';
        contextMenuData = [targetElementId];
        updateSelection([targetElementId]);
      }

      const container = event.currentTarget.container();
      const containerRect = container.getBoundingClientRect();

      setTempState({
        contextMenuData,
        contextMenuTarget,
        contextMenuPosition: {
          top: containerRect.top + event.currentTarget.getPointerPosition().y,
          left: containerRect.left + event.currentTarget.getPointerPosition().x,
        },
      });
    } else {
      setTempState({
        contextMenuData: null,
        contextMenuTarget: null,
        contextMenuPosition: null,
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMouseDownCapture = useCallback((event) => {
    // 如果是按下鼠标中间，等同于按下空格
    if (event.button === 1) {
      setTempState({ spacePressing: true });
    }
  }, []);

  const handleMouseUp = useCallback((event) => {
    setTempState({ spacePressing: false });

    setTimeout(() => {
      setSharedData('dragging-element', null);
      setSharedData('dragging-offset', null);
    }, 10);

    if (event.target.matches('input, textarea')) return;

    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleContenxtMenu = useCallback((event) => {
    event.evt.preventDefault();
  }, []);

  const handleKeyDown = useCallback((event) => {
    if (!event.target.matches('input, textarea')) {
      if (event.code === 'Space') {
        setTempState({ spacePressing: true });
      } else if (event.code === 'ShiftLeft' || event.code === 'ShiftRight') {
        setTempState({ shiftPressing: true });
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleKeyUp = useCallback((event) => {
    if (event.code === 'Space') {
      setTempState({ spacePressing: false });
    } else if (event.code === 'ShiftLeft' || event.code === 'ShiftRight') {
      setTempState({ shiftPressing: false });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 画布缩放设置
  const handleWheel = useCallback(
    (event) => {
      if (event.target && event.target.closest) {
        if (event.target.closest('[data-proxy-wheel="false"]')) {
          return;
        }
      }

      // 这里其实有一个非常有效的流畅性提升的方法，那就是不直接改viewport状态，而是实时改Stage的x/y/scale，操作完成之后再去实际更新状态
      // 但是这样的问题就是需要实时依赖viewport状态的地方会拿不到实时的值，如果产品能接受在画布拖动/缩放的时候隐藏一些UI元素，那么这个优化是可行的
      // 另外也可以把一些overlayer元素放到Stage内部用Konva来实现，这样缩放和移动就是跟着Stage的了，但是这样对于组件的显示效果会有一些限制

      const rawEvent = event.evt || event;

      rawEvent.preventDefault();

      if (!rawEvent.ctrlKey && !rawEvent.metaKey) {
        const x = stageRef.current.x() - rawEvent.deltaX; //* viewport.scale;
        const y = stageRef.current.y() - rawEvent.deltaY; //* viewport.scale;

        setViewport({ x, y });
      } else {
        // 按住ctrl键是缩放画布(mac触控板双指缩放也会触发)
        const prevScale = stageRef.current.scaleX();
        const pointer = stageRef.current.getPointerPosition();

        if (!pointer) return;

        const mousePointTo = {
          x: (pointer.x - stageRef.current.x()) / prevScale,
          y: (pointer.y - stageRef.current.y()) / prevScale,
        };

        let zoomScaleBy = ZOOM_SCALE_BY;
        if (lastZoomingAt && Date.now() - lastZoomingAt < 20) zoomScaleBy = 1.05;

        lastZoomingAt = Date.now();

        const direction = rawEvent.deltaY < 0 ? 1 : -1;
        const nextScale = Math.max(
          MIN_SCALE,
          Math.min(MAX_SCALE, direction > 0 ? prevScale * zoomScaleBy : prevScale / zoomScaleBy),
        );
        const nextX = pointer.x - mousePointTo.x * nextScale;
        const nextY = pointer.y - mousePointTo.y * nextScale;

        setViewport({ x: nextX, y: nextY, scale: nextScale });
      }
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleDeleteElement = useCallback(() => {
    const editingElementId = getProjectState('editingElementId');
    const currentElementTool = getProjectState('currentElementTool');

    // 如果正在编辑元素，则不删除
    if (editingElementId && currentElementTool) return;

    const seletion = getProjectState('selection');

    removeElements(seletion);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePaste = useCallback(async (event) => {
    if (Array.isArray(getSharedData('clipboard'))) return;

    if (event.clipboardData.files.length > 0) {
      try {
        setSharedData('clipboard', null);
        await insertLocalImage(event.clipboardData.files[0], { from: 'canvas' });
        notifySuccess(translate('upload_success_toast'));
      } catch (error) {
        console.log(error);
        notifyError(translate('upload_fail_toast'), error.message || error.msg || error);
      } finally {
        // ...
      }
    }
  }, []);

  const handleGlobalKeyDown = (event) => {
    if (event.ctrlKey || event.metaKey) {
      if (event.key === '=' || event.key === '-') {
        if (event.target.matches('input, textarea')) {
          event.preventDefault();
          event.stopPropagation();
        }
      } else {
        if (event.target.matches('input, textarea')) return;

        if (event.key === 'c') {
          // 清除剪贴板中的文件, 需要https或者localhost才生效
          navigator.clipboard?.writeText('');

          const elements = getProjectState('elements');
          const selection = getProjectState('selection');

          if (selection.length) {
            setSharedData('clipboard', selection);
          }

          if (selection.length >= 1) {
            const element = elements.find((item) => item.id === selection[0]);

            if (element?.type === 'image') {
              copyImageToClipboard(element.url);
            }
          }
        } else if (event.key === 'v') {
          setTimeout(() => {
            const clipboardElements = getSharedData('clipboard');
            if (Array.isArray(clipboardElements)) {
              duplicateElements(clipboardElements);
            }
          }, 100);
        }
      }
    }
  };

  // 旋转的功能暂时挂起
  // const handleRotateElement = useCallback((e) => {
  //   // console.log('xxx e', e)
  //   const {x : canvasOffsetX, y : canvasOffsetY, scale} = viewport
  //   const {offsetX: canvasScreenX , offsetY :canvasScreenY} = e.evt
  //   // console.log('xxx canvasScreenX, canvasScreenY',canvasScreenX, canvasScreenY)
  //   const hotRotateBuffer = 10 / scale
  //   const [canvasX, canvasY] = [
  //     canvasScreenX / scale - canvasOffsetX / scale,
  //     canvasScreenY / scale - canvasOffsetY / scale
  //   ]
  //   const range = getSelectionRange()

  //   const [rangeCenterX, rangeCenterY] = [(range.minX + range.maxX)/2, (range.minY + range.maxY)/2]
  //   const [rangeWidth,rangeHeight]= [range.maxX - range.minX, range.maxY - range.minY]
  //   const [detaX, detaY] = [canvasX-rangeCenterX, canvasY-rangeCenterY]

  //   // console.log('xxx detaX, detaY',detaX, detaY)
  //   if(
  //     Math.abs(detaX) < rangeWidth/2+hotRotateBuffer && Math.abs(detaX) > rangeWidth/2 &&
  //     Math.abs(detaY) < rangeHeight/2 + hotRotateBuffer && Math.abs(detaY) > rangeHeight/2
  //   ) {
  //     console.log('xxx xxx')
  //   }
  // },[viewport])

  const scale = { x: viewport.scale, y: viewport.scale };

  const cursorClassName = canvasDragging
    ? dragging
      ? 'cursor-grabbing'
      : 'cursor-grab'
    : currentCanvasTool === 'pencil'
      ? 'cursor-pencil'
      : '';

  useHotkeys([['backspace', handleDeleteElement]], ['INPUT', 'TEXTAREA']);
  useHotkeys([['delete', handleDeleteElement]], ['INPUT', 'TEXTAREA']);

  // 把挂件层的滚轮事件也传递到画布层进行处理
  useEventListener('wheel', handleWheel, {
    target: document.querySelector('[data-proxy-wheel="true"]'),
    passive: false,
  });

  useEventListener('keydown', handleKeyDown);
  useEventListener('keyup', handleKeyUp);
  useEventListener('paste', handlePaste, { capture: true });
  useEventListener('mousedown', handleMouseDownCapture, { capture: true });
  useEventListener('mouseup', handleMouseUp);

  useEffect(() => {
    sharedStage = stageRef.current;
    document.body.addEventListener('wheel', handleGlobalWheel, { passive: false });
    document.body.addEventListener('keydown', handleGlobalKeyDown);

    setTimeout(() => {
      mc('tool.canvas.show', { mode: getTempState('globalMode') });
    }, 100);

    return () => {
      sharedStage = null;
      document.body.removeEventListener('wheel', handleGlobalWheel);
      document.body.removeEventListener('keydown', handleGlobalKeyDown);
    };
  }, []);

  return (
    <div
      id="canvas-stage-wrapper"
      className="relative overflow-hidden"
      onDragOver={eventPreventDefault}
      onDrop={handleGlobalDrop}
    >
      <Stage
        // todos fqy 旋转的功能暂时挂起
        // onMouseMove={(e) => {
        //   const {selection} = getProjectState()
        //   if(!selection.length){
        //     return
        //   }
        //   handleRotateElement(e)
        // }}
        scale={scale}
        x={viewport.x}
        y={viewport.y}
        ref={stageRef}
        dragButtons={dragButtons}
        width={props.width}
        height={props.height}
        draggable={canvasDragging}
        onWheel={handleWheel}
        onClick={handleClick}
        onDragStart={handleDragStart}
        onDragMove={handleDragMove}
        onDrageEnd={handleDragEnd}
        onContextMenu={handleContenxtMenu}
        className={cursorClassName}
      >
        {props.children}
        <Layer name="extraShapes" />
      </Stage>
      <CustomCursor />
    </div>
  );
};
